"use client";

import { useMemo } from "react";
import { BalanceByToken, Decimal, MarginMode, WalletType } from "@cede/types";
import { API_ENDPOINT, EXCHANGES } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { CexPerpsBalance } from "./types";
import { useCexPerpsMarginAssetMode } from "./useCexPerpsMarginAssetMode";
import { useApi } from "@/hooks/core/useApi";
import { useExternalInvalidation } from "@/hooks/core/useExternalInvalidation";
import { useHubUser } from "@/hooks/user/useHubUser";
import { createExternalSetStore } from "@/utils/query";
import { useQuery, QueryClient } from "@tanstack/react-query";

interface UseCexPerpsBalancesParams {
  exchangeId: string;
  accountId: string | undefined;
  /** Settlement token for the selected market (e.g., USDT, USDC, VBUSDC). Required because balance lookup is keyed by this exact symbol. */
  settlementToken: string;
  /** Margin mode - cross margin will aggregate all token balances converted to settlement token */
  marginMode?: MarginMode;
  enabled?: boolean;
  refetchInterval?: number;
}

const CEX_PERPS_BALANCE_QUERY_KEY = "cex-perps-balances";

const balancesInvalidationStore = createExternalSetStore();

/** Tracks exchanges that need forceRefresh on next balance fetch (e.g. after a trade) */
const pendingForceRefresh = new Set<string>();

export function invalidateCexPerpsBalances(queryClient: QueryClient, exchangeId: string) {
  pendingForceRefresh.add(exchangeId);
  balancesInvalidationStore.add(exchangeId);
  return queryClient.invalidateQueries({ queryKey: [CEX_PERPS_BALANCE_QUERY_KEY, exchangeId] });
}

/**
 * Hook to fetch perpetual futures balance for a CEX exchange
 * Exchange-agnostic: pass any supported exchangeId
 *
 * For cross margin mode: aggregates all token balances converted to the settlement token
 * For isolated margin mode: returns only the settlement token balance
 */
export function useCexPerpsBalances({
  exchangeId,
  accountId,
  settlementToken,
  marginMode = MarginMode.ISOLATED,
  enabled = true,
  refetchInterval = 10_000,
}: UseCexPerpsBalancesParams) {
  const { auth } = useHubUser();
  const apiHelper = useApi();
  // Futures collateral model (single vs multi), fetched from its own endpoint. Undefined ⇒ "single".
  const assetMode = useCexPerpsMarginAssetMode(exchangeId, accountId) ?? "single";

  const {
    data: rawBalanceData,
    isLoading: isBalanceLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [CEX_PERPS_BALANCE_QUERY_KEY, exchangeId, accountId, settlementToken],
    queryFn: async () => {
      if (!auth.identityToken) {
        throw new Error("Not authenticated");
      }

      if (!accountId) {
        throw new Error("Account ID is required");
      }

      const additionalQueryParams: { walletTypes?: string; forceRefresh?: string } = {};

      if (exchangeId === EXCHANGES.CRYPTOCOM) {
        additionalQueryParams.walletTypes = WalletType.USD_M_FUTURES.toString();
      }

      if (pendingForceRefresh.has(exchangeId)) {
        additionalQueryParams.forceRefresh = "true";
        pendingForceRefresh.delete(exchangeId);
      }

      const response = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_BALANCES, {
        queryParams: {
          accountId: accountId.toString(),
          ...additionalQueryParams,
        },
      });
      // Response format: { [accountId]: { [walletType]: { [tokenSymbol]: balance } } }
      const accountBalances = response[accountId];

      return {
        accountBalances: accountBalances ?? null,
      };
    },
    enabled: enabled && isPerpsExchange(exchangeId) && !!accountId && !!auth.identityToken && !!settlementToken,
    staleTime: 10_000,
    refetchInterval,
  });

  const { isInvalidating } = useExternalInvalidation(balancesInvalidationStore, exchangeId, isFetching);

  /**
   * Computes the balance for a given quote asset and margin mode.
   *
   * This function is exposed separately from the memoized `balance` value to allow
   * other hooks (like `useCexPerpsPositions`) to compute balances for different
   * margin tokens without causing circular dependencies or redundant API calls.
   *
   * Use case: When calculating effective leverage for cross-margin positions,
   * we need the total balance in the position's margin token (e.g., USDT, BTC),
   * which may differ from the currently selected market's settlement token.
   *
   * @param quoteAsset - The token symbol to compute balance for (e.g., "USDT", "USD", "BTC")
   * @param marginMode - The margin mode (CROSS aggregates all tokens, ISOLATED returns only the quote asset)
   * @returns CexPerpsBalance with available, used, total, currency, and unrealizedPnl
   */
  const getBalance = (quoteAsset: string, marginMode: MarginMode): CexPerpsBalance => {
    const defaultBalance: CexPerpsBalance = {
      available: "0.00",
      used: "0.00",
      total: "0.00",
      currency: quoteAsset,
      unrealizedPnl: "0.00",
    };

    if (!rawBalanceData?.accountBalances) {
      return defaultBalance;
    }

    const { accountBalances } = rawBalanceData;

    // Check futures wallet first, then fall back to unified wallet
    const walletTypes = [WalletType.FUTURES, WalletType.UNIFIED, WalletType.USD_M_FUTURES];
    let walletBalances: BalanceByToken | null = null;

    for (const walletType of walletTypes) {
      if (accountBalances[walletType]) {
        walletBalances = accountBalances[walletType];
        break;
      }
    }

    if (!walletBalances) {
      return defaultBalance;
    }

    // "multi" accounts (Bybit/Crypto.com/Kraken always; Binance in Multi-Assets Mode) pool collateral
    // across assets, so cross-margin available is the sum of every collateral token's USD value.
    // "single" accounts (Bitget/Katana/LBank; Binance in Single-Asset Mode; the long tail) silo margin
    // per settlement token and take the direct read below. assetMode comes from useCexPerpsMarginAssetMode.
    if (marginMode === MarginMode.CROSS && assetMode === "multi") {
      // Cross margin: aggregate all token crossMarginBalanceUsd (already in USD, accounts for haircuts)
      let totalAvailable = new Decimal(0);
      let totalUsed = new Decimal(0);
      let total = new Decimal(0);

      for (const [, tokenBalance] of Object.entries(walletBalances)) {
        if (!tokenBalance.isAllowedAsCollateral) {
          continue;
        }
        const crossMargin = tokenBalance.crossMarginBalanceUsd;
        if (crossMargin) {
          totalAvailable = totalAvailable.add(new Decimal(crossMargin.free));
          totalUsed = totalUsed.add(new Decimal(crossMargin.used));
          total = total.add(new Decimal(crossMargin.total));
        }
      }
      return {
        available: totalAvailable.toFixed(2),
        used: totalUsed.toFixed(2),
        total: total.toFixed(2),
        currency: quoteAsset,
        unrealizedPnl: "0.00",
      };
    } else {
      // Isolated margin: only return the settlement token balance
      // Also Cross margin only for Bitget
      const assetBalance = walletBalances[quoteAsset];
      if (assetBalance) {
        return {
          available: assetBalance.freeBalance.toFixed(2),
          used: assetBalance.usedBalance.toFixed(2),
          total: assetBalance.totalBalance.toFixed(2),
          currency: quoteAsset,
          unrealizedPnl: "0.00",
        };
      }
      return defaultBalance;
    }
  };

  // Compute balance based on margin mode
  const balance = useMemo(
    (): CexPerpsBalance => getBalance(settlementToken, marginMode),
    [rawBalanceData, marginMode, settlementToken, exchangeId, assetMode],
  );

  return {
    getBalance,
    balance,
    available: balance.available,
    used: balance.used,
    total: balance.total,
    unrealizedPnl: balance.unrealizedPnl ?? "0.00",
    currency: balance.currency,
    isLoading: isBalanceLoading || isInvalidating,
    error: error as Error | null,
    refetch,
  };
}
